import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { RemindersApi } from "../api/RemindersApi";
import { logger } from "../utils/logger";
import { getErrorMessage } from "../utils/errorMessages";
import { toast } from "sonner";

const RemindersContext = createContext();

export const useReminders = () => {
  return useContext(RemindersContext);
};

export const RemindersProvider = ({ children }) => {
  const [reminders, setReminders] = useState([]);

  const fetchReminders = useCallback(async () => {
    try {
      const response = await RemindersApi.getReminders();
      setReminders(response.reminders);
    } catch (error) {
      const correlationId = error.response?.headers?.["x-correlation-id"];
      const errorMessage = getErrorMessage(error);

      logger.error("Failed to fetch reminders", error, { correlationId });

      toast.error(errorMessage);
    }
  }, []);

  const addReminder = async (reminder) => {
    try {
      const response = await RemindersApi.addReminder(reminder);
      setReminders(response.reminders);
    } catch (error) {
      const correlationId = error.response?.headers?.["x-correlation-id"];
      const errorMessage = getErrorMessage(error);

      logger.error("Failed to add reminder", error, { correlationId });

      toast.error(errorMessage);
    }
  };

  const updateReminder = async (reminderId, updatedFields) => {
    try {
      const response = await RemindersApi.updateReminder(
        reminderId,
        updatedFields,
      );
      setReminders(response.reminders);
    } catch (error) {
      const correlationId = error.response?.headers?.["x-correlation-id"];
      const errorMessage = getErrorMessage(error);

      logger.error("Failed to update reminder", error, {
        correlationId,
        reminderId,
      });

      toast.error(errorMessage);
    }
  };

  const deleteReminder = async (reminderId) => {
    const previousReminders = reminders;

    setReminders((prevReminders) =>
      prevReminders.filter((reminder) => reminder.reminderId !== reminderId),
    );

    try {
      await RemindersApi.deleteReminder(reminderId);
    } catch (error) {
      const correlationId = error.response?.headers?.["x-correlation-id"];
      const errorMessage = getErrorMessage(error);

      logger.error("Failed to delete reminder", error, {
        correlationId,
        reminderId,
      });

      toast.error(errorMessage);

      // Rollback to previous state
      setReminders(previousReminders);
    }
  };

  const value = {
    reminders,
    fetchReminders,
    addReminder,
    updateReminder,
    deleteReminder,
  };

  useEffect(() => {
    fetchReminders();
  }, [fetchReminders]);

  return (
    <RemindersContext.Provider value={value}>
      {children}
    </RemindersContext.Provider>
  );
};
